'use client'
import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Calendar, Users, CreditCard, MessageCircle, Trophy, HandHelping, LayoutDashboard, ClipboardList, DollarSign, Paintbrush, Code2 } from 'lucide-react'

type Menu = 'product' | 'coaches' | 'clubs' | null

export const MarketingNav = () => {
  const [scrolled,  setScrolled]  = useState(false)
  const [textMode,  setTextMode]  = useState<'light' | 'dark'>('light')
  const [open, setOpen]           = useState<Menu>(null)
  // Sprint 61.58 — mobile sheet state. The desktop nav was wrapped in
  // `hidden md:flex` with no hamburger fallback, so the three persona
  // links were invisible on mobile entirely. Mirroring LandingNav's
  // pattern: state + hamburger + slide-down sheet.
  const [mobileOpen, setMobileOpen] = useState(false)
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const pathname = usePathname()
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMobile() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [mobileOpen, closeMobile])

  // Close the mobile sheet when navigation route changes (clicking a
  // link inside the sheet shouldn't leave the sheet open for the next
  // page render).
  useEffect(() => { setMobileOpen(false) }, [pathname])

  // Single generic Get Started CTA everywhere (no persona-specific CTA branching).
  const ctaHref = '/signup'
  const ctaLabel = 'Get Started →'

  useEffect(() => {
    const heroEl = (
      document.querySelector('[data-section="hero"]') ||
      document.querySelector('section:first-of-type')
    ) as HTMLElement | null

    const handleScroll = () => {
      const scrollY    = window.scrollY
      const heroHeight = heroEl ? heroEl.offsetHeight : 600

      setScrolled(scrollY > 10)
      setTextMode(scrollY < heroHeight - 60 ? 'light' : 'dark')
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const openDD  = (m: NonNullable<Menu>) => { clearTimeout(timers.current[m]); setOpen(m) }
  const closeDD = (m: NonNullable<Menu>) => {
    timers.current[m] = setTimeout(() => setOpen(p => p === m ? null : p), 140)
  }

  const isLight  = textMode === 'light'
  const textColor  = isLight ? 'rgba(255,255,255,0.9)' : '#13201B'
  const hoverClass = isLight ? 'hover:text-white'       : 'hover:text-chalk-dark'
  const logoColor  = isLight ? '#ffffff'                 : '#13201B'

  // Scrolled bg: chalkboard green-black while over the hero/dark sections (matches
  // bg-chalkboard-grad hero — NOT navy, Direction A), white over light sections.
  const navBg = scrolled
    ? isLight
      ? 'bg-chalkboard'
      : 'bg-white/96 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.08)] border-b border-[rgba(10,37,64,0.07)]'
    : 'bg-[rgba(19,32,27,0.4)] backdrop-blur-sm'

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center gap-6">

        {/* Logo */}
        <Link href="/" className="text-[22px] font-bold shrink-0 leading-none transition-colors duration-300" style={{ fontFamily: 'var(--font-caveat)', color: logoColor }}>
          chalk<span style={{ color: '#F5E642' }}>.</span>
        </Link>

        {/* Center nav.
         *
         *  Sprint 61.26 — persona-aware nav per marketing-agent finding:
         *  the coach dropdowns ("How it works", "For directors") leaked
         *  coach + club CTAs into the /for-trainers page, contaminating
         *  the trainer-acquisition story with off-persona content. When
         *  the visitor is on a trainer marketing surface, suppress those
         *  dropdowns and surface trainer-relevant nav items instead.
         *  "For coaches" link stays present (cross-persona awareness is
         *  fine; the leak was the OPENED dropdown showing coach features
         *  on a trainer-focused page). */}
        <div className="hidden md:flex items-center gap-0.5 flex-1">
          {/* Persona-explicit nav, IDENTICAL on every page (coaches · clubs ·
              trainers). Previously /for-trainers swapped to a different set of
              links (How it works / Pricing / For coaches), which made the top
              nav change mid-session — removed for consistency. The trainer
              surface is fully built + live, so it stays a first-class nav item. */}
          <DropItem label="For coaches" menuKey="coaches" open={open === 'coaches'} onOpen={() => openDD('coaches')} onClose={() => closeDD('coaches')} isLight={isLight}>
            <CoachesDropdown onClose={() => setOpen(null)} />
          </DropItem>

          <DropItem label="For clubs" menuKey="clubs" open={open === 'clubs'} onOpen={() => openDD('clubs')} onClose={() => closeDD('clubs')} isLight={isLight}>
            <ClubsDropdown onClose={() => setOpen(null)} />
          </DropItem>

          <Link
            href="/for-trainers"
            className="text-[13px] font-medium px-4 py-2 rounded-full transition-all duration-200 hover:opacity-80"
            style={{ color: isLight ? 'rgba(255,255,255,0.9)' : '#13201B' }}
          >
            For trainers
          </Link>
        </div>

        {/* Right side — Sprint 61.59 fix: on mobile, ONLY the hamburger.
            Sign in + Get Started CTA hidden below md so the hamburger
            owns the right edge cleanly (no big yellow pill competing
            for attention next to a small icon button — that was the
            "not organized correctly" critique). CTAs live inside the
            mobile sheet instead. */}
        <div className="flex items-center gap-2 ml-auto">
          <Link
            href="/login"
            className={`hidden md:block text-[13px] font-medium px-3 py-2 transition-colors duration-300 ${hoverClass}`}
            style={{ color: textColor }}
          >
            Sign in
          </Link>
          <Link
            href={ctaHref}
            className="hidden md:inline-flex bg-chalk-yellow-hover text-chalk-dark text-[13px] font-bold rounded-full px-5 py-2.5 hover:bg-chalk-yellow transition-colors whitespace-nowrap"
          >
            {ctaLabel}
          </Link>

          {/* Hamburger — mobile only. Fixed Sprint 61.59:
              - items-center + justify-center: bars visually centered
                in the 44×44 click target (Sprint 61.58 version had
                them top-left aligned).
              - z-[60]: above any overlay (cookie banner, toasts) so
                the click is never intercepted.
              - Mirrors the LandingNav pattern that works in production. */}
          <button
            type="button"
            className="md:hidden relative z-[60] flex flex-col items-center justify-center gap-[5px] rounded-lg transition-colors hover:bg-white/5"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            style={{ width: 44, height: 44 }}
          >
            <span
              className="block w-5 h-0.5 transition-all duration-200 origin-center"
              style={{
                background: isLight ? '#ffffff' : '#13201B',
                transform: mobileOpen ? 'rotate(45deg) translateY(7px)' : 'none',
              }}
            />
            <span
              className="block w-5 h-0.5 transition-opacity duration-200"
              style={{
                background: isLight ? '#ffffff' : '#13201B',
                opacity:    mobileOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-5 h-0.5 transition-all duration-200 origin-center"
              style={{
                background: isLight ? '#ffffff' : '#13201B',
                transform: mobileOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>
    </header>

      {/* Mobile sheet — Sprint 61.61 fix: rendered as a SIBLING of
          <header>, not a child. The header carries `backdrop-blur-sm`
          (backdrop-filter), which in Chrome/Safari creates a new
          containing block for position:fixed descendants. When the
          sheet was a child of header, its `top-16 bottom-0` resolved
          against the header's 64px box, computing to height: 0 (i.e.
          invisible despite being in the DOM — the bug the user hit).
          Hoisting the sheet to the document level lets its
          position:fixed resolve against the viewport like it should.

          Sprint 61.59 (z-50, role=dialog) preserved. Body-scroll lock
          + Esc-to-close + route-change auto-close all unchanged. */}
      {mobileOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className="md:hidden fixed inset-x-0 top-16 bottom-0 z-50 bg-chalkboard overflow-y-auto"
        >
          <nav className="px-6 py-6 flex flex-col gap-1">
            <Link
              href="/for-coaches"
              onClick={closeMobile}
              className="block px-3 py-4 rounded-xl text-white text-lg font-semibold hover:bg-white/5 transition-colors"
              style={{ minHeight: 48 }}
            >
              For coaches
              <span className="block text-sm font-normal mt-0.5" style={{ color: '#9FB0A6' }}>
                Schedule · roster · payments · chat · Game Day
              </span>
            </Link>
            <Link
              href="/for-clubs"
              onClick={closeMobile}
              className="block px-3 py-4 rounded-xl text-white text-lg font-semibold hover:bg-white/5 transition-colors"
              style={{ minHeight: 48 }}
            >
              For clubs
              <span className="block text-sm font-normal mt-0.5" style={{ color: '#9FB0A6' }}>
                Multi-team dashboard · tryouts · finances · white label
              </span>
            </Link>
            <Link
              href="/for-trainers"
              onClick={closeMobile}
              className="block px-3 py-4 rounded-xl text-white text-lg font-semibold hover:bg-white/5 transition-colors"
              style={{ minHeight: 48 }}
            >
              For trainers
              <span className="block text-sm font-normal mt-0.5" style={{ color: '#9FB0A6' }}>
                Booking · payouts · recurring auto-bill · embed widget
              </span>
            </Link>

            <div className="border-t border-white/10 my-4" />

            <Link
              href="/pricing"
              onClick={closeMobile}
              className="block px-3 py-3 rounded-xl text-base font-medium hover:bg-white/5 transition-colors"
              style={{ color: 'rgba(255,255,255,0.7)', minHeight: 44 }}
            >
              Pricing
            </Link>

            <div className="flex flex-col gap-3 pt-6">
              <Link
                href={ctaHref}
                onClick={closeMobile}
                className="flex items-center justify-center bg-chalk-yellow-hover text-chalk-dark font-bold text-base rounded-2xl py-4 hover:bg-chalk-yellow transition-colors"
                style={{ minHeight: 48 }}
              >
                {ctaLabel}
              </Link>
              <Link
                href="/login"
                onClick={closeMobile}
                className="flex items-center justify-center text-base font-medium py-3"
                style={{ color: 'rgba(255,255,255,0.55)', minHeight: 44 }}
              >
                Sign in
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  )
}

// ── Dropdown wrapper ──────────────────────────────────────────────────────────

function DropItem({
  label, menuKey, open, onOpen, onClose, children, isLight,
}: {
  label: string; menuKey: string; open: boolean; isLight: boolean
  onOpen: () => void; onClose: () => void; children: React.ReactNode
}) {
  const color       = isLight ? 'rgba(255,255,255,0.9)' : '#13201B'
  const activeColor = isLight ? 'rgba(255,255,255,0.9)' : '#13201B'
  const hoverBg     = isLight ? 'rgba(255,255,255,0.12)' : 'rgba(7,20,40,0.05)'

  return (
    <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button
        className="flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-full transition-all duration-200"
        style={{ color: open ? activeColor : color, background: open ? hoverBg : 'transparent' }}
      >
        {label}
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <path d="M2.5 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div
        className="absolute top-[calc(100%+8px)] left-1/2 bg-white border border-[rgba(10,37,64,0.09)] rounded-[20px] shadow-[0_8px_40px_rgba(10,37,64,0.14)] p-2 z-[500]"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        style={{
          opacity: open ? 1 : 0,
          transform: open
            ? 'translateX(-50%) translateY(0px) scale(1)'
            : 'translateX(-50%) translateY(-6px) scale(0.97)',
          transition: 'opacity 0.18s cubic-bezier(0.22, 1, 0.36, 1), transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ── Coaches dropdown ──────────────────────────────────────────────────────────

const COACH_ITEMS = [
  { label: 'Scheduling & RSVP',     desc: 'Games, practices, calendar export', bg: '#FEF9C3', accent: '#a16207', Icon: Calendar,       href: '/for-coaches#schedule'  },
  { label: 'Roster & profiles',      desc: 'Players, guardians, waivers',       bg: '#DCFCE7', accent: '#166534', Icon: Users,          href: '/for-coaches#roster'   },
  { label: 'Payments & dues',        desc: 'Collect dues with reminders',       bg: '#DBEAFE', accent: '#1e40af', Icon: CreditCard,     href: '/for-coaches#payments' },
  { label: 'Chat & announcements',   desc: 'Threads, 1:1 parent messaging',     bg: '#EDE9FE', accent: '#5b21b6', Icon: MessageCircle,  href: '/for-coaches#chat'     },
]

function CoachesDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div className="w-[540px]">
      <Link href="/for-coaches#game-day" onClick={onClose} className="block bg-[#F7FAFF] rounded-xl px-4 py-3 mb-1.5 hover:bg-blue-50 transition-colors">
        <div className="inline-flex items-center gap-1.5 bg-[rgba(245,230,66,0.15)] border border-[rgba(245,230,66,0.3)] rounded-full px-2.5 py-0.5 text-[10px] font-bold text-[#7a6200] mb-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-chalk-yellow-hover" />
          Free on every tier
        </div>
        <div className="text-[13px] font-bold text-chalk-dark mb-0.5">Game Day — a scoreboard that stays out of the way</div>
        <div className="text-[11px] text-slate-500">Simple home / away scoring for every sport — same clean UI every weekend</div>
      </Link>
      <div className="grid grid-cols-2 gap-1 mb-1.5">
        <div className="col-span-2 text-[10px] font-bold text-slate-500 uppercase tracking-[0.07em] px-3.5 pt-2 pb-1">Team management</div>
        {COACH_ITEMS.map(item => (
          <Link key={item.label} href={item.href} onClick={onClose} className="flex items-start gap-3 px-3.5 py-2.5 rounded-xl hover:bg-[#F7FAFF] transition-colors">
            <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ background: item.bg }}>
              <item.Icon size={16} color={item.accent} />
            </div>
            <div>
              <div className="text-[13px] font-semibold text-chalk-dark mb-0.5 leading-tight">{item.label}</div>
              <div className="text-[11px] text-slate-500 leading-snug">{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
      <div className="border-t border-[#F0F4F8] pt-2.5 pb-1 px-1.5 flex items-center justify-between">
        <Link href="/signup" onClick={onClose}><button className="bg-chalk-yellow-hover text-chalk-dark text-[12px] font-bold rounded-full px-4 py-2">Start free as a coach →</button></Link>
        <Link href="/for-coaches" onClick={onClose} className="text-[12px] text-slate-500 hover:text-chalk-dark flex items-center gap-1 transition-colors">View all features <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5h7M6 3l3 2.5L6 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg></Link>
      </div>
    </div>
  )
}

// ── Clubs dropdown ────────────────────────────────────────────────────────────

const CLUB_ITEMS = [
  { label: 'Club dashboard',     desc: 'All teams and coaches in one view',  bg: '#EFF6FF', accent: '#1e40af', Icon: LayoutDashboard, href: '/for-clubs#dashboard'   },
  { label: 'Tryout management',  desc: 'Evaluate, rank, send offers in-app', bg: '#F0FDF4', accent: '#166534', Icon: ClipboardList,   href: '/for-clubs#tryouts'     },
  { label: 'Payments & revenue', desc: 'Collect fees, instant payouts',       bg: '#FFF7ED', accent: '#c2410c', Icon: DollarSign,      href: '/for-clubs#finances'    },
  { label: 'White label',        desc: 'Your domain, your brand',            bg: '#F5F3FF', accent: '#5b21b6', Icon: Paintbrush,      href: '/for-clubs#embed'       },
  { label: 'Embed widgets',      desc: 'Live schedule + scores on your site',bg: '#FEF9C3', accent: '#a16207', Icon: Code2,           href: '/for-clubs#embed'       },
]

function ClubsDropdown({ onClose }: { onClose: () => void }) {
  return (
    <div className="w-[560px]">
      <Link href="/for-clubs#tournaments" onClick={onClose} className="block bg-[#F7FAFF] rounded-xl px-4 py-3 mb-1.5 hover:bg-blue-50 transition-colors">
        <div className="inline-flex items-center gap-1.5 bg-[rgba(245,230,66,0.15)] border border-[rgba(245,230,66,0.3)] rounded-full px-2.5 py-0.5 text-[10px] font-bold text-[#7a6200] mb-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-chalk-yellow-hover" />
          Run tournaments end to end
        </div>
        <div className="text-[13px] font-bold text-chalk-dark mb-0.5">Create, monetize, and fill your tournaments</div>
        <div className="text-[11px] text-slate-500">Divisions, entry fees, promo codes, instant payouts, featured listing</div>
      </Link>
      <div className="grid grid-cols-2 gap-1 mb-1.5">
        <div className="col-span-2 text-[10px] font-bold text-slate-500 uppercase tracking-[0.07em] px-3.5 pt-2 pb-1">Organization</div>
        {CLUB_ITEMS.map(item => (
          <Link key={item.label} href={item.href} onClick={onClose} className="flex items-start gap-3 px-3.5 py-2.5 rounded-xl hover:bg-[#F7FAFF] transition-colors">
            <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ background: item.bg }}>
              <item.Icon size={16} color={item.accent} />
            </div>
            <div>
              <div className="text-[13px] font-semibold text-chalk-dark mb-0.5 leading-tight">{item.label}</div>
              <div className="text-[11px] text-slate-500 leading-snug">{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
      <div className="border-t border-[#F0F4F8] pt-2.5 pb-1 px-1.5 flex items-center justify-between">
        <Link href="/org/signup" onClick={onClose}><button className="bg-chalkboard text-white text-[12px] font-bold rounded-full px-4 py-2">Set up your organization →</button></Link>
        <Link href="/for-clubs" onClick={onClose} className="text-[12px] text-slate-500 hover:text-chalk-dark flex items-center gap-1 transition-colors">View org features <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5h7M6 3l3 2.5L6 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg></Link>
      </div>
    </div>
  )
}
